/* eslint-disable max-lines */

import {
    EmbedEvent,
    NotificationEvent as EmbedNotificationEvent,
} from 'embed/types/events'
import {
    Listener,
    NotificationEvent,
    NotificationKey,
    NotificationQueueEntry,
    NotificationType,
} from './types'
import {
    NinchatEvents,
    NinchatProperties,
} from '../../api/types'
import React, {
    useCallback,
    useContext,
    useEffect,
    useRef,
} from 'react'
import {
    effectPick,
    effectProcess,
} from './effects'
import {
    useAppDispatch,
    useAppSelectorWithShallowEquality,
} from '../../hooks'
import Button from 'components/Button'
import {EmbedContext} from 'components/EmbedContext'
import {NinchatEventActionPayload} from '../../api/NinchatClient'
import {PlatformContext} from 'components/PlatformContext'
import {Toast} from 'react-hot-toast'
import {addAppListener} from '../../middlewares/listener'
import {notificationIcon} from '../Icon'
import {store} from '../../store'
import {styled} from 'stitches/config'
import {toast} from '../Toast'
import {useRealms} from 'hooks/ninchat'
import {useSendToParent} from 'embed/utils/event'
import {useTranslation} from 'react-i18next'

const StyledNotification = styled('div', {
    alignItems: 'center',
    display: 'flex',
    gap: '10px',
})

const BrowserNotification: React.FC = (): React.ReactElement | null => {
    const embed = useContext(EmbedContext)
    const platform = useContext(PlatformContext)

    const notificationSettings = useAppSelectorWithShallowEquality(({ninchat}) => ninchat.user_settings.notifications)

    const realms = useRealms()

    const audioRef = useRef<HTMLAudioElement>(null)
    const ignoreIdleRef = useRef<boolean>()
    const listenersRef = useRef(new Map<string, Listener>())
    const notificationQueueLockRef = useRef(false)
    const notificationQueueRef = useRef<NotificationQueueEntry[]>([])
    const previousNotificationSettingsRef = useRef<NinchatProperties.Notifications>({})
    const requireInteractionRef = useRef<boolean>()
    const shownToastErrorsRef = useRef<string[]>([])
    const sendToParent = useSendToParent()

    const dispatch = useAppDispatch()
    const {t} = useTranslation()

    const toastError = (message: string) => {
        if (shownToastErrorsRef.current.includes(message)) {
            return
        }

        toast.error(message)

        shownToastErrorsRef.current.push(message)
    }

    const parseEntry = (entry: NotificationQueueEntry): NotificationEvent => {
        const {payload} = entry

        const event: NotificationEvent = {
            type: entry.type,
            ...entry.audio && {audio: entry.audio},
            ...entry.desktop && {desktop: entry.desktop},
        }

        switch (entry.type) {
            case NotificationType.MessageReceivedChannel: {
                const {
                    event: {
                        channel_id = '',
                        message_user_name: userName = '',
                    },
                    payload: {text},
                } = payload as NinchatEventActionPayload<NinchatEvents.NinchatMessageReceivedEvent>

                const channelName = store.getState().ninchat.user_channels[channel_id]?.channel_attrs.name ?? ''

                event.channel_name = channelName
                event.message = text
                event.user_name = userName

                return event
            }
            case NotificationType.MessageReceivedChannelAudience: {
                const {
                    event: {
                        channel_id = '',
                        message_user_name,
                    },
                    payload: {
                        text,
                        user_name,
                    },
                } = payload as NinchatEventActionPayload<NinchatEvents.NinchatMessageReceivedEvent>

                const userName = message_user_name ?? user_name

                const queue_id = store.getState().ninchat.user_channels[channel_id]?.channel_attrs.queue_id ?? ''
                const queueName = store.getState().ninchat.user_queues?.[queue_id]?.queue_attrs.name ?? ''

                event.message = text
                event.queue_name = queueName
                event.user_name = userName

                return event
            }
            case NotificationType.MessageReceivedChannelJoin: {
                const {
                    event: {channel_id = ''},
                    payload: {user_name: userName = ''},
                } = payload as NinchatEventActionPayload<NinchatEvents.NinchatMessageReceivedEvent>

                const channelName = store.getState().ninchat.user_channels[channel_id]?.channel_attrs.name ?? ''

                event.channel_name = channelName
                event.user_name = userName

                return event
            }
            case NotificationType.MessageReceivedDialogue: {
                const {
                    event: {message_user_name: userName = ''},
                    payload: {text},
                } = payload as NinchatEventActionPayload<NinchatEvents.NinchatMessageReceivedEvent>

                event.message = text
                event.user_name = userName

                return event
            }
            case NotificationType.QueueActivity:
                return event
            case NotificationType.QueueUpdatedAudienceAcceptPeer: {
                const {
                    queue_attrs: {name: queueName = ''},
                    realm_id = '',
                } = payload as NinchatEvents.NinchatQueueUpdatedEvent

                const realmName = realms[realm_id]?.realm_attrs.name ?? ''

                event.queue_name = queueName
                event.realm_name = realmName

                return event
            }
            case NotificationType.QueueUpdatedAudienceRequest: {
                const {
                    queue_attrs: {name: queueName = ''},
                    realm_id = '',
                } = payload as NinchatEvents.NinchatQueueUpdatedEvent

                const realmName = realms[realm_id]?.realm_attrs.name ?? ''

                event.queue_name = queueName
                event.realm_name = realmName

                return event
            }
            default:
                return event
        }
    }

    const getNotification = (event: NotificationEvent): Notification => {
        switch (event.type) {
            case 'message_received/channel': {
                return {
                    ...event.message && {body: event.message},
                    title: `${event.user_name} @ ${event.channel_name}`,
                } as Notification
            }
            case 'message_received/channel/audience': {
                return {
                    ...event.message && {body: event.message},
                    title: `${event.user_name ? `${event.user_name} @ ` : ''}${event.queue_name}`,
                } as Notification
            }
            case 'message_received/channel/join': {
                return {
                    body: t('{{user_name}} joined channel {{channel_name}}', event),
                    title: t('User joined channel'),
                } as Notification
            }
            case 'message_received/dialogue': {
                return {
                    ...event.message && {body: event.message},
                    title: `${event.user_name}`,
                } as Notification
            }
            case 'queue_activity':
                return {title: t('New registered contact')} as Notification
            case 'queue_updated/audience_accept_peer': {
                return {
                    body: t('Visitor picked from audience queue {{queue_name}} @ {{realm_name}}', event),
                    title: t('Visitor picked from queue'),
                } as Notification
            }
            case 'queue_updated/audience_request': {
                return {
                    body: t('New visitor in audience queue {{queue_name}} @ {{realm_name}}', event),
                    title: t('Visitor in queue'),
                } as Notification
            }
            default:
                return {title: t('New notification')} as Notification
        }
    }

    const playNotification = (entry: NotificationQueueEntry) => {
        if (platform === 'embed-web') {
            const targetOrigin = embed?.targetOrigin

            if (!targetOrigin) {
                return
            }

            const event: EmbedNotificationEvent = {
                audio: true,
                event: EmbedEvent.Notification,
                targetOrigin,
                type: entry.type,
            }

            sendToParent(event)
            return
        }

        if (audioRef.current) {
            audioRef.current.pause()
            audioRef.current.currentTime = 0
            audioRef.current.play()
                .catch((e) => {
                    if (e.name === 'NotAllowedError') {
                        toastError(t('Audio notification not allowed to play'))
                    } else {
                        toastError(t('Unable to play audio notification'))
                    }
                })
        }
    }

    const requestPermission = (_t: Toast) => {
        Notification.requestPermission().then((permission) => {
            toast.dismiss(_t.id)

            if (permission === 'granted') {
                toast.success(t('Notifications enabled'))
            } else {
                toast.error(t('Notifications are not enabled'))
            }
        })
    }

    const showNotification = (entry: NotificationQueueEntry) => {
        const event = parseEntry(entry)

        if (platform === 'embed-web') {
            const targetOrigin = embed?.targetOrigin

            if (!targetOrigin) {
                return
            }

            delete event.audio
            delete event.message

            const embedEvent: EmbedNotificationEvent = {
                event: EmbedEvent.Notification,
                targetOrigin,
                ...event,
            }

            sendToParent(embedEvent)
            return
        }

        if (!('Notification' in window)) {
            toastError(t('This browser does not support desktop notifications'))
            return
        }

        if (Notification.permission === 'denied') {
            toastError(t('Desktop notification not allowed to show'))
            return
        } else if (Notification.permission !== 'granted') {
            toast((_t) => (
                <StyledNotification>
                    <span>{t('You have to enable desktop notifications')}</span> <Button onPress={() => requestPermission(_t)} text={t('Enable notifications')} type='button' />
                </StyledNotification>
            ), {duration: 60000})
            return
        }

        const {
            body, title,
        } = getNotification(event)

        const notification = new Notification(title, {
            body,
            icon: notificationIcon(),
            requireInteraction: requireInteractionRef.current,
        })
        notification.onerror = () => toastError(t('Unable to show desktop notification'))
    }

    const flushNotificationQueue = useCallback(() => {
        if (notificationQueueLockRef.current) {
            return
        }

        notificationQueueLockRef.current = true

        while (notificationQueueRef.current.length) {
            const entry = notificationQueueRef.current.shift() as NotificationQueueEntry

            if (entry.audio) {
                playNotification(entry)
            }

            if (entry.desktop) {
                showNotification(entry)
            }
        }

        notificationQueueLockRef.current = false
    }, [])

    useEffect(() => () => {
        for (const listener of listenersRef.current.values()) {
            listener.unsubscribe()
        }
        listenersRef.current.clear()
        previousNotificationSettingsRef.current = {}
    }, [])

    useEffect(() => {
        if (notificationSettings) {
            for (const [key, value] of Object.entries(notificationSettings) as [NotificationKey, boolean][]) {
                if (previousNotificationSettingsRef.current[key] !== value) {
                    if (key === 'ignore_idle') {
                        ignoreIdleRef.current = value
                    }
                    if (key === 'require_interaction') {
                        requireInteractionRef.current = value
                    }

                    const isEnabled = value

                    const {
                        type, effect,
                    } = effectPick(key)

                    if (type && effect) {
                        if (isEnabled && !listenersRef.current.has(type)) {
                            const unsubscribe = dispatch(addAppListener({
                                effect: (action, listenerApi) => effectProcess({
                                    action,
                                    flushNotificationQueue,
                                    ignoreIdle: ignoreIdleRef.current,
                                    listener: listenersRef.current.get(type),
                                    listenerApi,
                                    notificationQueue: notificationQueueRef.current,
                                }),
                                type,
                            }))

                            listenersRef.current.set(type, {
                                effects: new Map(),
                                unsubscribe,
                            })
                        }

                        const listener = listenersRef.current.get(type)

                        if (listener) {
                            if (!isEnabled && listener.effects.has(key)) {
                                listener.effects.delete(key)
                            } else if (isEnabled) {
                                listener.effects.set(key, effect)
                            }

                            if (!listener.effects.size) {
                                listener.unsubscribe()
                                listenersRef.current.delete(type)
                            }
                        }
                    }
                }
            }

            previousNotificationSettingsRef.current = notificationSettings
        }
    }, [notificationSettings])

    if (platform !== 'web') {
        return null
    }

    return (
        <div style={{visibility: 'hidden'}} >
            <audio autoPlay={false} preload='auto' ref={audioRef}>
                <source src='https://s3-eu-west-1.amazonaws.com/ninchat.com/asset/plip.ogg' type='audio/ogg' />
                <source src='https://s3-eu-west-1.amazonaws.com/ninchat.com/asset/plip.mp3' type='audio/mpeg' />
            </audio>
        </div>
    )
}

export default BrowserNotification
